import { useTranslation } from 'react-i18next'
import { AppIcon } from './ui/icon'
import { mdiEyeOutline, mdiEyeOffOutline } from '@mdi/js'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import { useCan, type PermissionBearingEntity } from '../contexts/PermissionsContext'
import type { Permission } from '../api/schema'

/**
 * The audience badge (spec 2026-09-04-03, decision 7).
 *
 * Audience is a property of the RESOURCE, not of the role: a manager decides
 * what residents may see, one document, intervention or issue at a time. The
 * badge is therefore always visible — reading "is this shared?" is part of
 * understanding the row — and only the toggle is gated on `write`.
 *
 * `visibility` is the effective value the API resolved (a document with no
 * override inherits its type's `shared_by_default` flag), so the badge never
 * has to re-derive it.
 */
export function VisibilityBadge({
    visibility,
    writePermission,
    entity,
    onChange,
    testId = 'visibility-badge',
}: {
    visibility: string | null | undefined
    /** The permission the toggle needs, e.g. `documents.write`. Omit to render read-only. */
    writePermission?: Permission
    /** The row itself, so the check runs on its own permissions. */
    entity?: PermissionBearingEntity | null
    /** Called with the next value. Omit to render read-only. */
    onChange?: (next: 'internal' | 'shared') => void
    testId?: string
}) {
    const { t } = useTranslation('access')
    const shared = visibility === 'shared'
    const canWrite = useCan(writePermission, entity)
    const interactive = Boolean(onChange) && canWrite && Boolean(writePermission)

    const label = shared ? t('visibility.shared') : t('visibility.internal')
    const hint = shared ? t('visibility.shared_hint') : t('visibility.internal_hint')
    const icon = shared ? mdiEyeOutline : mdiEyeOffOutline

    if (!interactive) {
        return (
            <Badge
                variant={shared ? 'default' : 'secondary'}
                title={hint}
                data-testid={testId}
            >
                <AppIcon path={icon} size="xs" className="mr-1" />
                {label}
            </Badge>
        )
    }

    const next = shared ? 'internal' : 'shared'
    const action = shared ? t('visibility.toggle_to_internal') : t('visibility.toggle_to_shared')

    return (
        <Button
            variant={shared ? 'default' : 'secondary'}
            size="sm"
            className="h-6 px-2 text-xs"
            aria-label={action}
            title={`${hint} — ${action}`}
            data-testid={`${testId}-toggle`}
            onClick={(event) => {
                event.stopPropagation()
                onChange?.(next)
            }}
        >
            <AppIcon path={icon} size="sm" className="mr-1" />
            {label}
        </Button>
    )
}
